/**
 * StructuurDialog — de commissieafspraak van een platform-partner beheren.
 *
 * Drie modi, één per config-verb (api/config/*, runbook docs/api/config-domein.md):
 *  - 'aanmaken'   partner-structuur-aanmaken   (met partnerkiezer als er nog geen partner is)
 *  - 'wijzigen'   partner-tarief-wijzigen      (sluit de lopende af, opent de nieuwe)
 *  - 'beeindigen' partner-structuur-beeindigen (einddatum)
 *
 * Elke actie loopt eerst als dry-run: de guards draaien, de voorgenomen
 * wijziging komt terug en pas daarna is de bevestigknop echt. Dat is niet
 * alleen netjes, het is hier nodig — dit schrijft naar Salesforce en de
 * commissie hangt eraan.
 *
 * Waarom tarief wijzigen geen simpel invulveld is: de commissie-berekening
 * matcht op de rekendatum van vandaag, niet op de datum van de order. Het
 * bedrag overschrijven herprijst dus met terugwerkende kracht. Het verb sluit
 * daarom de oude structuur af en opent een nieuwe per ingangsdatum, en deze
 * dialoog laat dat ook zo zien.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  configVerb,
  zoekPartnerKandidaten,
  nlDatum,
  DOORVERWIJSBARE_SEGMENTEN,
  SEGMENTEN,
  type ConfigResultaat,
  type PartnerKandidaat,
  type PartnerStructuur,
} from '../../lib/platform';

export type StructuurModus = 'aanmaken' | 'wijzigen' | 'beeindigen';

interface Props {
  modus: StructuurModus;
  /** Leeg bij 'aanmaken' vanaf het overzicht: dan kiest de gebruiker de partner. */
  partnerSfId?: string;
  partnerNaam?: string | null;
  /** Alleen bij 'wijzigen'/'beeindigen'. */
  structuur?: PartnerStructuur;
  onSluiten: () => void;
  onKlaar: (melding: string) => void;
}

const TITEL: Record<StructuurModus, string> = {
  aanmaken: 'Verdienmodel toevoegen',
  wijzigen: 'Tarief wijzigen',
  beeindigen: 'Afspraak beëindigen',
};

const VERB: Record<StructuurModus, string> = {
  aanmaken: 'partner-structuur-aanmaken',
  wijzigen: 'partner-tarief-wijzigen',
  beeindigen: 'partner-structuur-beeindigen',
};

/** Vandaag in Amsterdam als YYYY-MM-DD — zelfde rekendatum als de server. */
function vandaagAms(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Amsterdam',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

const veldKlasse =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-indigo-500';

export function StructuurDialog({
  modus,
  partnerSfId,
  partnerNaam,
  structuur,
  onSluiten,
  onKlaar,
}: Props) {
  const vandaag = vandaagAms();

  const [gekozenPartner, setGekozenPartner] = useState<PartnerKandidaat | null>(
    partnerSfId ? { sfId: partnerSfId, naam: partnerNaam ?? null, type: null, plaats: null, ccEmail: null } : null
  );
  const [zoek, setZoek] = useState('');
  const [kandidaten, setKandidaten] = useState<PartnerKandidaat[]>([]);
  const [zoekBezig, setZoekBezig] = useState(false);

  const [segment, setSegment] = useState(structuur?.segment ?? 'SR');
  const [type, setType] = useState<'Fixed' | 'Percentage'>(
    (structuur?.type ?? '').toLowerCase().includes('perc') ? 'Percentage' : 'Fixed'
  );
  const [waarde, setWaarde] = useState('');
  const [ingangsdatum, setIngangsdatum] = useState(vandaag);
  const [einddatum, setEinddatum] = useState(vandaag);
  const [notities, setNotities] = useState('');
  const [terugwerkendBevestigd, setTerugwerkendBevestigd] = useState(false);

  const [preview, setPreview] = useState<ConfigResultaat | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);

  // Esc sluit, tenzij er een write loopt.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !bezig) onSluiten();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [bezig, onSluiten]);

  // Partnerzoek met een korte adempauze, zodat niet elke toetsaanslag een call is.
  useEffect(() => {
    if (modus !== 'aanmaken' || gekozenPartner || zoek.trim().length < 2) {
      setKandidaten([]);
      return;
    }
    let afgebroken = false;
    setZoekBezig(true);
    const t = setTimeout(async () => {
      try {
        const r = await zoekPartnerKandidaten(zoek);
        if (!afgebroken) setKandidaten(r);
      } catch {
        if (!afgebroken) setKandidaten([]);
      } finally {
        if (!afgebroken) setZoekBezig(false);
      }
    }, 250);
    return () => {
      afgebroken = true;
      clearTimeout(t);
    };
  }, [zoek, gekozenPartner, modus]);

  const bouwBody = useCallback(
    (dryRun: boolean): Record<string, unknown> => {
      if (modus === 'beeindigen') {
        return {
          structuurId: structuur?.sfId,
          geldigTot: einddatum,
          terugwerkendBevestigd,
          dryRun,
        };
      }
      return {
        partnerId: gekozenPartner?.sfId,
        segment,
        commissieType: type,
        commissieWaarde: Number(waarde.replace(',', '.')),
        geldigVan: ingangsdatum,
        ...(notities.trim() ? { notities: notities.trim() } : {}),
        ...(modus === 'wijzigen' ? { terugwerkendBevestigd } : {}),
        dryRun,
      };
    },
    [modus, structuur, einddatum, gekozenPartner, segment, type, waarde, ingangsdatum, notities, terugwerkendBevestigd]
  );

  const doeDryRun = useCallback(async () => {
    setBezig(true);
    setFout(null);
    setPreview(null);
    try {
      setPreview(await configVerb(VERB[modus], bouwBody(true)));
    } catch (e) {
      setFout(e instanceof Error ? e.message : String(e));
    } finally {
      setBezig(false);
    }
  }, [modus, bouwBody]);

  const doeBevestig = useCallback(async () => {
    setBezig(true);
    setFout(null);
    try {
      const r = await configVerb(VERB[modus], bouwBody(false));
      const waarschuwingen = r.data?.waarschuwingen ?? [];
      onKlaar(
        waarschuwingen.length > 0
          ? `Opgeslagen, met ${waarschuwingen.length} aandachtspunt${waarschuwingen.length > 1 ? 'en' : ''}.`
          : 'Opgeslagen.'
      );
    } catch (e) {
      setFout(e instanceof Error ? e.message : String(e));
      setPreview(null);
    } finally {
      setBezig(false);
    }
  }, [modus, bouwBody, onKlaar]);

  const waardeNum = Number(waarde.replace(',', '.'));
  // Nul is een geldig tarief: gratis leads terwijl de afspraak nog loopt. De
  // server staat dat sinds 07-08 toe (en waarschuwt erover); deze check stond
  // nog op "boven nul" en hield de knop dan uit.
  const waardeGeldig = waarde.trim() !== '' && Number.isFinite(waardeNum) && waardeNum >= 0;
  const compleet =
    modus === 'beeindigen'
      ? Boolean(structuur?.sfId && einddatum)
      : Boolean(gekozenPartner && segment && waardeGeldig && ingangsdatum);

  const terugwerkend = modus === 'beeindigen' ? einddatum < vandaag : ingangsdatum < vandaag;
  const waarschuwingen = (preview?.data?.waarschuwingen as string[] | undefined) ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h3 className="text-base font-semibold text-slate-900">{TITEL[modus]}</h3>
          <button
            onClick={onSluiten}
            disabled={bezig}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50"
            aria-label="Sluiten"
          >
            ✕
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          {/* ── Partner ── */}
          {modus === 'aanmaken' && (
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Partner</label>
              {gekozenPartner ? (
                <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <div className="text-sm">
                    <span className="font-medium text-slate-900">{gekozenPartner.naam ?? gekozenPartner.sfId}</span>
                    {gekozenPartner.plaats && <span className="text-slate-500"> · {gekozenPartner.plaats}</span>}
                    {gekozenPartner.ccEmail === null && !partnerSfId && (
                      <p className="text-xs text-amber-700">Geen CC-mailadres op dit account</p>
                    )}
                  </div>
                  {!partnerSfId && (
                    <button
                      onClick={() => {
                        setGekozenPartner(null);
                        setZoek('');
                      }}
                      className="text-xs text-slate-500 hover:text-slate-700"
                    >
                      wijzig
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <input
                    value={zoek}
                    onChange={(e) => setZoek(e.target.value)}
                    placeholder="Zoek op bedrijfsnaam…"
                    className={veldKlasse}
                    autoFocus
                  />
                  {zoekBezig && <p className="mt-1 text-xs text-slate-400">Zoeken…</p>}
                  {kandidaten.length > 0 && (
                    <ul className="mt-1 max-h-48 overflow-y-auto rounded-lg border border-slate-200">
                      {kandidaten.map((k) => (
                        <li key={k.sfId}>
                          <button
                            onClick={() => setGekozenPartner(k)}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                          >
                            <span className="font-medium text-slate-900">{k.naam ?? k.sfId}</span>
                            {k.plaats && <span className="text-slate-500"> · {k.plaats}</span>}
                            {!k.ccEmail && <span className="ml-1 text-xs text-amber-700">geen CC-mail</span>}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {!zoekBezig && zoek.trim().length >= 2 && kandidaten.length === 0 && (
                    <p className="mt-1 text-xs text-slate-500">
                      Geen account gevonden. Een partner moet eerst als account in Salesforce bestaan.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {modus !== 'aanmaken' && (
            <p className="text-sm text-slate-600">
              {partnerNaam ?? partnerSfId} · {structuur?.segment}
            </p>
          )}

          {/* ── Beëindigen ── */}
          {modus === 'beeindigen' ? (
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Laatste geldige dag</label>
              <input
                type="date"
                value={einddatum}
                onChange={(e) => setEinddatum(e.target.value)}
                className={veldKlasse}
              />
              <p className="mt-1 text-xs text-slate-500">
                Na deze datum heeft {partnerNaam ?? 'deze partner'} geen geldige afspraak meer voor{' '}
                {structuur?.segment} en verdwijnt hij voor dat segment uit de doorverwijs-lijst.
              </p>
            </div>
          ) : (
            <>
              {/* ── Segment ── */}
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-500">Segment</label>
                <select
                  value={segment}
                  onChange={(e) => setSegment(e.target.value)}
                  disabled={modus === 'wijzigen'}
                  className={`${veldKlasse} bg-white disabled:bg-slate-50 disabled:text-slate-500`}
                >
                  {SEGMENTEN.map((s) => (
                    <option key={s} value={s}>
                      {s}
                      {DOORVERWIJSBARE_SEGMENTEN.includes(s) ? '' : ' (niet doorverwijsbaar)'}
                    </option>
                  ))}
                </select>
                {modus === 'aanmaken' && !DOORVERWIJSBARE_SEGMENTEN.includes(segment) && (
                  <p className="mt-1 text-xs text-amber-700">
                    Voor dit segment bestaat geen anker-product, dus de partner verschijnt er niet mee in de
                    inbox-actie "Doorsturen naar partner". De afspraak wordt wel vastgelegd en meegeteld.
                  </p>
                )}
              </div>

              {/* ── Tarief ── */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-500">Verdienmodel</label>
                  <select
                    value={type}
                    onChange={(e) => setType(e.target.value as 'Fixed' | 'Percentage')}
                    className={`${veldKlasse} bg-white`}
                  >
                    <option value="Fixed">Vast bedrag per aanvraag</option>
                    <option value="Percentage">Percentage van de orderwaarde</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-500">
                    {type === 'Fixed' ? 'Bedrag (excl. btw)' : 'Percentage'}
                  </label>
                  <div className="relative">
                    {type === 'Fixed' && (
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">€</span>
                    )}
                    <input
                      value={waarde}
                      onChange={(e) => setWaarde(e.target.value)}
                      inputMode="decimal"
                      placeholder={type === 'Fixed' ? '12,50' : '10'}
                      className={`${veldKlasse} ${type === 'Fixed' ? 'pl-7' : 'pr-7'}`}
                    />
                    {type === 'Percentage' && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">%</span>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Ingangsdatum ── */}
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-500">Gaat in op</label>
                <input
                  type="date"
                  value={ingangsdatum}
                  onChange={(e) => setIngangsdatum(e.target.value)}
                  className={veldKlasse}
                />
                {modus === 'wijzigen' && (
                  <p className="mt-1 text-xs text-slate-500">
                    De huidige afspraak ({structuur?.segment}) loopt tot de dag ervoor door. Het oude tarief blijft
                    dus gelden voor alles daarvóór.
                  </p>
                )}
              </div>

              {modus === 'aanmaken' && (
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-500">
                    Notitie <span className="text-slate-400">(optioneel)</span>
                  </label>
                  <input
                    value={notities}
                    onChange={(e) => setNotities(e.target.value)}
                    placeholder="bv. afgesproken per mail 07-08"
                    className={veldKlasse}
                  />
                </div>
              )}
            </>
          )}

          {/* ── Terugwerkende kracht ── */}
          {terugwerkend && modus !== 'aanmaken' && (
            <label className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <input
                type="checkbox"
                checked={terugwerkendBevestigd}
                onChange={(e) => setTerugwerkendBevestigd(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Deze datum ligt in het verleden. Dat herprijst elke herberekening van orders vanaf die datum. Vink
                aan als dat de bedoeling is.
              </span>
            </label>
          )}

          {fout && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{fout}</div>
          )}

          {/* ── Dry-run-uitkomst ── */}
          {preview && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
              <p className="font-medium text-slate-900">Gecontroleerd, nog niet opgeslagen</p>
              {modus === 'wijzigen' && (
                <p className="mt-1 text-xs text-slate-600">
                  De lopende afspraak wordt afgesloten op {nlDatum(ingangsdatum)} min één dag; het nieuwe tarief
                  geldt vanaf {nlDatum(ingangsdatum)}.
                </p>
              )}
              {waarschuwingen.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs text-amber-800">
                  {waarschuwingen.map((w, i) => (
                    <li key={i}>· {w}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <button
            onClick={onSluiten}
            disabled={bezig}
            className="rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-50"
          >
            Annuleren
          </button>
          {!preview ? (
            <button
              onClick={() => void doeDryRun()}
              disabled={!compleet || bezig || (terugwerkend && modus !== 'aanmaken' && !terugwerkendBevestigd)}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {bezig ? 'Controleren…' : 'Controleren'}
            </button>
          ) : (
            <button
              onClick={() => void doeBevestig()}
              disabled={bezig}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {bezig ? 'Opslaan…' : 'Opslaan in Salesforce'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
